import { useRef } from "react";

import { WhatsNewFlyout } from "@bokio/components/Help/Help/components/WhatsNewFlyout/WhatsNewFlyout";
import { AppMessageType, useAppContext } from "@bokio/contexts/AppContext/AppContext";
import { useReleaseNotesContext } from "@bokio/contexts/ReleaseNotesContext/ReleaseNotesContext";
import { RatingToast } from "@bokio/designsystem/components/RatingToast/RatingToast";
import Markdown from "@bokio/elements/Markdown/Markdown";
import { AgencyAnalyticsEventCategory, useAgencyMetric } from "@bokio/hooks/useAgencyMetric/useAgencyMetric";
import { useModalStack } from "@bokio/hooks/useModalStack/useModalStack";
import { AccountingLangFactory, GeneralLangFactory } from "@bokio/lang";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import { getRoute } from "@bokio/shared/route";
import { formatMessage } from "@bokio/shared/utils/format";
import { mergeClassNames } from "@bokio/utils/classes";

import { Link } from "../../../core/elements/Link/Link";
import { Toast } from "../Toast/Toast";
import DetailedMessage from "./DetailedMessage.internals";

import type { AppMessage } from "@bokio/contexts/AppContext/AppContext";

import * as styles from "./appMessagesOverlay.scss";

interface AppMessagesOverlayProps {
	className?: string;
}

const inReverseOrder = <T,>(arr: T[]) => {
	const newarr = [...arr];
	newarr.reverse();
	return newarr;
};

interface MessageToasterProps {
	message: AppMessage;
}
import RatingType = m.Entities.Feedback.RatingType;

const MessageToaster = ({ message }: MessageToasterProps): React.JSX.Element => {
	const lang = GeneralLangFactory();
	const accountingLang = AccountingLangFactory();
	const { whatsNewData } = useReleaseNotesContext();
	const modals = useModalStack();
	const [addMetric] = useAgencyMetric();

	const isOpen = useRef(true);

	const app = useAppContext();
	const onClose = () => app.dismissMessage(message.key);

	const onModalClose = () => {
		app.dismissMessage(message.key);
		isOpen.current = false;
	};

	const onClickWhatNew = () => {
		modals.addModal(() => (
			<WhatsNewFlyout onClose={() => onModalClose()} isOpen={isOpen.current} whatsNewData={whatsNewData} />
		));
	};

	switch (message.type) {
		case AppMessageType.DetailedError:
			return (
				<Toast
					testId="AppMessage_DetailedError"
					title={message.title}
					severity="error"
					persist={message.persist ?? false}
					onClose={onClose}
				>
					<DetailedMessage content={message.content} referenceId={message.refrenceId} />
				</Toast>
			);
		case AppMessageType.ErrorMessage:
			return (
				<Toast severity="error" persist={message.persist ?? false} onClose={onClose} testId={message.testId}>
					<Markdown markdownContent={message.message} />
				</Toast>
			);
		case AppMessageType.SuccessMessage:
			return (
				<Toast
					severity="success"
					persist={message.persist ?? false}
					onClose={onClose}
					testId={message.testId}
					title={message.title}
				>
					<DetailedMessage content={message.content} />
				</Toast>
			);
		case AppMessageType.WarningMessage:
			return (
				<Toast severity="warning" persist={message.persist ?? false} onClose={onClose} testId={message.testId}>
					<span>{message.message}</span>
				</Toast>
			);
		case AppMessageType.InfoMessage:
			return (
				<Toast severity="info" persist={message.persist ?? false} onClose={onClose} testId={message.testId}>
					<span>{message.message}</span>
				</Toast>
			);
		case AppMessageType.NewAppVersion:
			return (
				<Toast testId="AppMessage_NewAppVersion" severity="info" persist={message.persist ?? false} onClose={onClose}>
					<span>
						{lang.NewUpdate_Text} <Link onClick={() => app.reloadForNewVersion("ToasterClick")}>{lang.Refresh}</Link>
					</span>
				</Toast>
			);
		case AppMessageType.BankSyncNotification:
			return (
				<Toast testId="BankFeed_SyncNotification" severity="info" persist={message.persist ?? false} onClose={onClose}>
					<span>
						{formatMessage(accountingLang.BankFeed_SyncMessage, text => (
							<Link onClick={onClose} route={getRoute("todo", { company: message.message.CompanyId })}>
								{text}
							</Link>
						))}
					</span>
				</Toast>
			);

		case AppMessageType.StripeNotification:
			return (
				<Toast severity="info" persist={message.persist ?? false} onClose={onClose}>
					<span>{message.message}</span>
				</Toast>
			);
		case AppMessageType.BookkeepingRating:
			return (
				<RatingToast
					className={mergeClassNames(styles.slideIn, styles.wrapper)}
					onClose={onClose}
					onCancel={message.onCancel}
					type={message.ratingType}
					renderStepTitle={message.renderStepTitle}
					renderStepDescription={message.renderStepDescription}
					additionalData={message.additionalData}
				/>
			);
		case AppMessageType.InvoicingRating:
			return (
				<RatingToast
					testId={"InvoiceRating"}
					className={mergeClassNames(styles.slideIn, styles.wrapper)}
					onClose={onClose}
					onCancel={message.onCancel}
					type={message.ratingType}
					renderStepTitle={message.renderStepTitle}
					renderStepDescription={message.renderStepDescription}
					additionalData={message.additionalData}
				/>
			);
		case AppMessageType.ClosureRating:
			return (
				<RatingToast
					testId={"ClosureRating"}
					className={mergeClassNames(styles.slideIn, styles.wrapper)}
					onClose={onClose}
					onCancel={message.onCancel}
					type={message.ratingType}
					renderStepTitle={message.renderStepTitle}
					renderStepDescription={message.renderStepDescription}
					additionalData={message.additionalData}
				/>
			);
		case AppMessageType.VacationYearEndRating:
			return (
				<RatingToast
					testId={"VacationYearEndRating"}
					className={mergeClassNames(styles.slideIn, styles.wrapper)}
					onClose={onClose}
					onCancel={message.onCancel}
					type={message.ratingType}
					renderStepTitle={message.renderStepTitle}
					renderStepDescription={message.renderStepDescription}
					additionalData={message.additionalData}
				/>
			);
		case AppMessageType.QuoteRating:
			return (
				<RatingToast
					testId={"QuotingRating"}
					className={mergeClassNames(styles.slideIn, styles.wrapper)}
					onClose={onClose}
					onCancel={message.onCancel}
					type={message.ratingType}
					renderStepTitle={message.renderStepTitle}
					renderStepDescription={message.renderStepDescription}
					additionalData={message.additionalData}
				/>
			);
		case AppMessageType.ReceivingEInvoiceRating:
			return (
				<RatingToast
					className={mergeClassNames(styles.slideIn, styles.wrapper)}
					onClose={onClose}
					onCancel={message.onCancel}
					type={message.ratingType}
					renderStepTitle={message.renderStepTitle}
					renderStepDescription={message.renderStepDescription}
					additionalData={message.additionalData}
				/>
			);
		case AppMessageType.CESFeedbackRating:
			return (
				<RatingToast
					testId={"CESFeedbackRating"}
					className={mergeClassNames(styles.slideIn, styles.wrapper)}
					onClose={onClose}
					onCancel={message.onCancel}
					type={message.ratingType}
					renderStepTitle={message.renderStepTitle}
					renderStepDescription={message.renderStepDescription}
					additionalData={message.additionalData}
				/>
			);
		case AppMessageType.AgencyCESFeedbackRating:
			return (
				<RatingToast
					testId={"AgencyCESFeedbackRating"}
					className={mergeClassNames(styles.slideIn, styles.wrapper)}
					onClose={onClose}
					onCancel={message.onCancel}
					type={RatingType.Unknown}
					partnerRatingType={message.ratingType}
					renderStepTitle={message.renderStepTitle}
					renderStepDescription={message.renderStepDescription}
					additionalData={message.additionalData}
				/>
			);
		case AppMessageType.TrialPlanNotification:
			return (
				<Toast testId={"TrialPlanToast"} severity="info" persist={message.persist ?? false} onClose={onClose}>
					<span>{message.message}</span>
				</Toast>
			);
		case AppMessageType.ReleaseNoteUpdatesInAgency:
			return (
				<Toast severity="info" testId={"AgencyReleaseNoteOverLay"} persist={message.persist ?? false} onClose={onClose}>
					{message.message}
					<Link
						onClick={async () => {
							message.onCloseWhatsNewFlyout();
							onClickWhatNew();
							addMetric(
								AgencyAnalyticsEventCategory.WhatsNewAgencyOverview,
								"Click read more",
								"WhatsNewAgencyOverview",
								1,
							);
						}}
						testId="AgencyReleaseNoteOverLay_Link"
					>
						{lang.ReadMore}
					</Link>
				</Toast>
			);
		case AppMessageType.ReleaseNoteUpdatesInCompany:
			return (
				<Toast
					severity="info"
					testId={"AgencyReleaseNoteOverLay_InCompany"}
					persist={message.persist ?? false}
					onClose={onClose}
				>
					{message.message}
					<Link
						onClick={() => {
							addMetric(
								AgencyAnalyticsEventCategory.WhatsNewCompanyOverview,
								"Click visit backoffice",
								"WhatsNewCompanyOverview",
								1,
								message.agencyId,
							);
						}}
						route={getRoute("agencyOverview", { agencyId: message.agencyId })}
					>
						{lang.Visit_Backoffice}
					</Link>
				</Toast>
			);
	}
};

export const AppMessagesOverlay = ({ className }: AppMessagesOverlayProps) => {
	const { messages } = useAppContext();

	return (
		<div className={mergeClassNames(styles.container, className)}>
			{inReverseOrder(messages).map(message => (
				<MessageToaster key={message.key} message={message} />
			))}
		</div>
	);
};
